import React from 'react';
import { MessageList } from './MessageList';
import { MessageInput } from './MessageInput';
import { ThreadSelector } from './ThreadSelector';
import { MessageThread } from './types';
import { useAuth } from '../../lib/auth-context';
import { collection, query, where, orderBy, onSnapshot } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { X, MessageSquare, Hash, Bug, Globe, MessagesSquare, Volume2, VolumeX, ChevronLeft, CheckCheck } from 'lucide-react';
import * as amplitude from '@amplitude/analytics-browser';
import { getCountriesFromFirebase } from '../../lib/firebase-service';
import { useIsMobile } from '../../hooks/useIsMobile';
import { updateThreadVisit, hasNewMessagesSinceLastVisit, getLastThreadVisit, markAllThreadsAsRead } from '../../lib/thread-visits-service';
import { motion } from 'framer-motion';

interface MessageCenterProps {
  isOpen: boolean;
  onClose: () => void;
  countryCode?: string;
  initialThread?: { code: string; type: 'country' | 'general' | 'bug' };
}

export function MessageCenter({ isOpen, onClose, countryCode, initialThread }: MessageCenterProps) {
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const [isClosing, setIsClosing] = React.useState(false);
  const [activeThread, setActiveThread] = React.useState<MessageThread>(
    initialThread
      ? { code: initialThread.code, type: initialThread.type }
      : countryCode
        ? { code: countryCode, type: 'country' }
        : { code: 'general_discussion', type: 'general' }
  );
  const [messages, setMessages] = React.useState<any[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [mobileView, setMobileView] = React.useState<'threads' | 'conversation'>(
    initialThread ? 'conversation' : 'threads'
  );
  const [replyingTo, setReplyingTo] = React.useState<{ id: string; userEmail: string; username: string; userId: string } | null>(null);
  const [countryName, setCountryName] = React.useState<string>('');
  const [refreshKey, setRefreshKey] = React.useState(0);
  const [hasNewMessages, setHasNewMessages] = React.useState(false);
  const messagesContainerRef = React.useRef<HTMLDivElement>(null);
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);
  const [lastSentMessageId, setLastSentMessageId] = React.useState<string | null>(null);
  const [soundEnabled, setSoundEnabled] = React.useState(() => {
    const saved = localStorage.getItem('mc_sound_enabled');
    return saved === null ? true : saved === 'true';
  });
  const [isMarkingAsRead, setIsMarkingAsRead] = React.useState(false);

  const toggleSound = () => {
    setSoundEnabled(prev => {
      const next = !prev;
      localStorage.setItem('mc_sound_enabled', String(next));
      return next;
    });
  };

  const handleMarkAllAsRead = async () => {
    if (!user?.uid || isMarkingAsRead) return;

    setIsMarkingAsRead(true);
    try {
      await markAllThreadsAsRead(user.uid);
      setRefreshKey(prev => prev + 1);
      amplitude.track('Message Center Mark All As Read');
    } catch (error) {
      console.error('Error marking all as read:', error);
    } finally {
      setIsMarkingAsRead(false);
    }
  };

  React.useEffect(() => {
    if (isOpen && isMobile && !initialThread) {
      setMobileView('threads');
    }
  }, [isOpen, isMobile, initialThread]);

  React.useEffect(() => {
    if (countryCode) {
      amplitude.track('Message Center Thread Changed', {
        threadCode: countryCode,
        threadType: 'country'
      });
      setActiveThread({ code: countryCode, type: 'country' });
    }
  }, [countryCode]);

  const handleClose = () => {
    setIsClosing(true);
    amplitude.track('Message Center Closed');
    setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, 250);
  };

  React.useEffect(() => {
    if (!isLoading && messagesContainerRef.current) {
      setTimeout(() => {
        if (messagesContainerRef.current) {
          messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
        }
      }, 100);
    }
  }, [isLoading, activeThread.code]);

  React.useEffect(() => {
    if (lastSentMessageId && messagesContainerRef.current) {
      setTimeout(() => {
        const messageElement = messagesContainerRef.current?.querySelector(`[data-message-id="${lastSentMessageId}"]`);
        if (messageElement) {
          messageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          setLastSentMessageId(null);
        }
      }, 500);
    }
  }, [lastSentMessageId, messages]);

  React.useEffect(() => {
    if (!isOpen) return;

    setIsLoading(true);
    const messagesRef = collection(db, 'messages');
    const q = query(
      messagesRef,
      where('thread_code', '==', activeThread.code),
      where('status', '==', 'active'),
      orderBy('created_at', 'desc')
    );

    const unsubscribe = onSnapshot(q, async (snapshot) => {
      const newMessages = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setMessages(newMessages);
      setIsLoading(false);

      if (user?.uid && activeThread.code) {
        const lastVisit = await getLastThreadVisit(user.uid, activeThread.code);
        const hasNew = await hasNewMessagesSinceLastVisit(activeThread.code, lastVisit, db);
        setHasNewMessages(hasNew);

        await updateThreadVisit(user.uid, activeThread.code);
        setRefreshKey(prev => prev + 1);
      }
    }, (error) => {
      console.error('Error in message subscription:', error);
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, [isOpen, activeThread.code, user?.uid]);

  React.useEffect(() => {
    const fetchCountryName = async () => {
      if (activeThread.type === 'country') {
        try {
          const countries = await getCountriesFromFirebase();
          const country = countries.find(c => c.code === activeThread.code);
          if (country) {
            setCountryName(country.name);
          }
        } catch (error) {
          console.error('Error fetching country name:', error);
        }
      }
    };

    fetchCountryName();
  }, [activeThread]);

  React.useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const getThreadTitle = () => {
    switch (activeThread.type) {
      case 'country':
        return countryName || 'Pays';
      case 'bug':
        return 'Signalements de bugs';
      default:
        return 'Discussion generale';
    }
  };

  const getThreadIcon = () => {
    switch (activeThread.type) {
      case 'country':
        return <Globe className="w-4 h-4 text-white" />;
      case 'bug':
        return <Bug className="w-4 h-4 text-white" />;
      default:
        return <Hash className="w-4 h-4 text-white" />;
    }
  };

  const getThreadGradient = () => {
    switch (activeThread.type) {
      case 'country':
        return 'from-emerald-500 to-teal-600';
      case 'bug':
        return 'from-orange-500 to-rose-500';
      default:
        return 'from-sky-500 to-blue-600';
    }
  };

  const showMobileThreads = isMobile && mobileView === 'threads';
  const showMobileConversation = isMobile && mobileView === 'conversation';

  return (
    <div
      className={`fixed inset-0 z-[100] transition-all duration-300 ease-out ${
        isClosing ? 'opacity-0' : 'opacity-100'
      }`}
    >
      <div className="absolute inset-0 bg-black/50 backdrop-blur-md" onClick={handleClose} />

      <div className="relative flex items-center justify-center h-full p-0 sm:p-5">
        <div
          className={`relative bg-white dark:bg-gray-950 w-full h-full sm:h-[88vh] sm:max-w-5xl sm:rounded-2xl overflow-hidden flex flex-col transition-all duration-300 ease-out ${
            isClosing
              ? 'scale-[0.97] opacity-0 translate-y-3'
              : 'scale-100 opacity-100 translate-y-0'
          } shadow-2xl shadow-black/20 dark:shadow-black/50 ring-1 ring-black/5 dark:ring-white/5`}
        >
          {/* Mobile: Thread list view */}
          {showMobileThreads && (
            <div className="flex flex-col h-full">
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-gray-800/80 bg-white dark:bg-gray-950">
                <div className="flex items-center gap-3">
                  <div className="relative w-9 h-9 rounded-xl bg-gradient-to-br from-sky-500 to-blue-600 flex items-center justify-center shadow-sm">
                    <MessagesSquare className="w-4 h-4 text-white" />
                  </div>
                  <h2 className="text-[15px] font-bold text-gray-900 dark:text-white">
                    Messages
                  </h2>
                </div>
                <div className="flex items-center gap-1">
                  <motion.button
                    onClick={handleMarkAllAsRead}
                    disabled={isMarkingAsRead}
                    whileTap={{ scale: 0.95 }}
                    className="relative p-2 rounded-xl transition-all duration-200 text-emerald-500 hover:text-emerald-600 dark:text-emerald-400 dark:hover:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 disabled:opacity-50 disabled:cursor-not-allowed group"
                    aria-label="Marquer tout comme lu"
                    title="Marquer tout comme lu"
                  >
                    {isMarkingAsRead ? (
                      <div className="w-4.5 h-4.5 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <CheckCheck className="w-4.5 h-4.5" />
                    )}
                  </motion.button>
                  <button
                    onClick={toggleSound}
                    className={`p-2 rounded-xl transition-all duration-200 ${
                      soundEnabled
                        ? 'text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                        : 'text-red-400 hover:text-red-500 dark:text-red-500 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20'
                    }`}
                    aria-label={soundEnabled ? 'Desactiver les sons' : 'Activer les sons'}
                  >
                    {soundEnabled ? <Volume2 className="w-4.5 h-4.5" /> : <VolumeX className="w-4.5 h-4.5" />}
                  </button>
                  <button
                    onClick={handleClose}
                    className="p-2 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-all duration-200"
                    aria-label="Fermer"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto bg-gray-50 dark:bg-gray-900 p-3 custom-scrollbar">
                <ThreadSelector
                  activeThread={activeThread}
                  onThreadChange={(thread) => {
                    setActiveThread(thread);
                    setMobileView('conversation');
                    setReplyingTo(null);
                  }}
                  countryCode={countryCode}
                  refreshKey={refreshKey}
                  soundEnabled={soundEnabled}
                />
              </div>
            </div>
          )}

          {/* Mobile: Conversation view */}
          {showMobileConversation && (
            <div className="flex flex-col h-full">
              <div className="flex items-center justify-between px-2 py-3 border-b border-gray-100 dark:border-gray-800/80 bg-white dark:bg-gray-950">
                <div className="flex items-center gap-1 min-w-0">
                  <button
                    onClick={() => setMobileView('threads')}
                    className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-colors flex-shrink-0"
                    aria-label="Retour aux canaux"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <div className={`relative w-8 h-8 rounded-xl bg-gradient-to-br ${getThreadGradient()} flex items-center justify-center shadow-sm flex-shrink-0`}>
                    {getThreadIcon()}
                  </div>
                  <div className="min-w-0">
                    <h2 className="text-[15px] font-bold text-gray-900 dark:text-white leading-tight truncate">
                      {getThreadTitle()}
                    </h2>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      {hasNewMessages && (
                        <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                      )}
                      <p className="text-[11px] text-gray-500 dark:text-gray-400 font-medium">
                        {messages.length} message{messages.length > 1 ? 's' : ''}
                      </p>
                    </div>
                  </div>
                </div>
                <button
                  onClick={handleClose}
                  className="p-2 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-all duration-200 flex-shrink-0"
                  aria-label="Fermer"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="flex-1 flex flex-col overflow-hidden bg-gradient-to-b from-gray-50/50 to-white dark:from-gray-900/50 dark:to-gray-950">
                {isLoading ? (
                  <div className="flex-1 flex flex-col items-center justify-center gap-3">
                    <div className="relative">
                      <div className="w-10 h-10 rounded-xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                        <MessageSquare className="w-5 h-5 text-gray-300 dark:text-gray-600" />
                      </div>
                      <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-white dark:bg-gray-950 flex items-center justify-center">
                        <div className="w-3 h-3 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                      </div>
                    </div>
                    <p className="text-xs text-gray-400 dark:text-gray-500 font-medium">Chargement...</p>
                  </div>
                ) : (
                  <div ref={messagesContainerRef} className="flex-1 overflow-y-auto custom-scrollbar">
                    <div ref={scrollContainerRef}>
                      <MessageList
                        messages={messages}
                        onReply={(message) => setReplyingTo({
                          id: message.id,
                          userEmail: message.user_email,
                          username: message.username,
                          userId: message.user_id
                        })}
                        soundEnabled={soundEnabled}
                      />
                    </div>
                  </div>
                )}

                <MessageInput
                  threadCode={activeThread.code}
                  threadType={activeThread.type}
                  userId={user?.uid || ''}
                  userEmail={user?.email || ''}
                  replyingTo={replyingTo}
                  onCancelReply={() => setReplyingTo(null)}
                  scrollContainerRef={messagesContainerRef}
                  onMessageSent={(messageId) => setLastSentMessageId(messageId)}
                  soundEnabled={soundEnabled}
                />
              </div>
            </div>
          )}

          {/* Desktop: Side-by-side layout */}
          {!isMobile && (
            <>
              <div className="relative flex items-center justify-between px-5 py-3 border-b border-gray-100 dark:border-gray-800/80 bg-white dark:bg-gray-950">
                <div className="flex items-center gap-3">
                  <div className={`relative w-9 h-9 rounded-xl bg-gradient-to-br ${getThreadGradient()} flex items-center justify-center shadow-sm`}>
                    {getThreadIcon()}
                  </div>
                  <div>
                    <h2 className="text-[15px] font-bold text-gray-900 dark:text-white leading-tight">
                      {getThreadTitle()}
                    </h2>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      {hasNewMessages && (
                        <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                      )}
                      <MessagesSquare className="w-3 h-3 text-gray-400 dark:text-gray-500" />
                      <p className="text-[11px] text-gray-500 dark:text-gray-400 font-medium">
                        {messages.length} message{messages.length > 1 ? 's' : ''}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-1">
                  <motion.button
                    onClick={handleMarkAllAsRead}
                    disabled={isMarkingAsRead}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="relative group flex items-center gap-2 px-3 py-2 rounded-xl transition-all duration-200 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 disabled:opacity-50 disabled:cursor-not-allowed"
                    aria-label="Marquer tout comme lu"
                  >
                    {isMarkingAsRead ? (
                      <>
                        <div className="w-4 h-4 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
                        <span className="text-xs font-semibold hidden sm:inline">Chargement...</span>
                      </>
                    ) : (
                      <>
                        <CheckCheck className="w-4 h-4" />
                        <span className="text-xs font-semibold hidden sm:inline">Tout marquer comme lu</span>
                      </>
                    )}
                  </motion.button>
                  <button
                    onClick={toggleSound}
                    className={`p-2 rounded-xl transition-all duration-200 ${
                      soundEnabled
                        ? 'text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                        : 'text-red-400 hover:text-red-500 dark:text-red-500 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20'
                    }`}
                    aria-label={soundEnabled ? 'Desactiver les sons' : 'Activer les sons'}
                  >
                    {soundEnabled ? <Volume2 className="w-4.5 h-4.5" /> : <VolumeX className="w-4.5 h-4.5" />}
                  </button>
                  <button
                    onClick={handleClose}
                    className="p-2 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-all duration-200"
                    aria-label="Fermer"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <div className="flex flex-1 overflow-hidden">
                <div className="w-72 bg-gray-50 dark:bg-gray-900 border-r border-gray-200 dark:border-gray-800">
                  <div className="p-3 h-full overflow-y-auto custom-scrollbar">
                    <ThreadSelector
                      activeThread={activeThread}
                      onThreadChange={(thread) => {
                        setActiveThread(thread);
                        setReplyingTo(null);
                      }}
                      countryCode={countryCode}
                      refreshKey={refreshKey}
                      soundEnabled={soundEnabled}
                    />
                  </div>
                </div>

                <div className="flex-1 flex flex-col overflow-hidden bg-gradient-to-b from-gray-50/50 to-white dark:from-gray-900/50 dark:to-gray-950">
                  {isLoading ? (
                    <div className="flex-1 flex flex-col items-center justify-center gap-3">
                      <div className="relative">
                        <div className="w-10 h-10 rounded-xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                          <MessageSquare className="w-5 h-5 text-gray-300 dark:text-gray-600" />
                        </div>
                        <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-white dark:bg-gray-950 flex items-center justify-center">
                          <div className="w-3 h-3 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                        </div>
                      </div>
                      <p className="text-xs text-gray-400 dark:text-gray-500 font-medium">Chargement...</p>
                    </div>
                  ) : (
                    <div ref={messagesContainerRef} className="flex-1 overflow-y-auto custom-scrollbar">
                      <div ref={scrollContainerRef}>
                        <MessageList
                          messages={messages}
                          onReply={(message) => setReplyingTo({
                            id: message.id,
                            userEmail: message.user_email,
                            username: message.username,
                            userId: message.user_id
                          })}
                          soundEnabled={soundEnabled}
                        />
                      </div>
                    </div>
                  )}

                  <MessageInput
                    threadCode={activeThread.code}
                    threadType={activeThread.type}
                    userId={user?.uid || ''}
                    userEmail={user?.email || ''}
                    replyingTo={replyingTo}
                    onCancelReply={() => setReplyingTo(null)}
                    scrollContainerRef={messagesContainerRef}
                    onMessageSent={(messageId) => setLastSentMessageId(messageId)}
                    soundEnabled={soundEnabled}
                  />
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
